import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { VehicleRental, VehicleRentalStatus, VehicleRentalPriority } from '../../../models/VehicleRental';
import { VehicleRentalService } from '../../../services/vehicle-rental.service';
import { ErrorService } from '../../../shared/services/error.service';
import { TableAction } from '../../../shared/components/table/table.component';
import { StatusService } from '../../../services/status.service';
import { Status } from '../../../models/Status';

@Component({
  selector: 'app-vehicle-rentals',
  templateUrl: './vehicle-rentals.component.html',
  styleUrls: ['./vehicle-rentals.component.css']
})
export class VehicleRentalsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private searchTimeout: any;

  // ===== DADOS =====
  vehicleRentals: VehicleRental[] = [];
  filteredVehicleRentals: VehicleRental[] = [];
  isLoading = false;
  error: string | null = null;

  // Paginação
  currentPage = 1;
  pageSize = 10;
  totalItems = 0;
  totalPages = 0;

  // ===== FILTROS =====
  filters = {
    status: '',
    priority: '',
    search: ''
  };

  // ===== DADOS DE APOIO =====
  statusOptions: { value: string; label: string }[] = [];
  loadingStatuses = false;

  priorityOptions = [
    { value: '', label: 'Todas as Prioridades' },
    { value: VehicleRentalPriority.BAIXA, label: 'Baixa' },
    { value: VehicleRentalPriority.NORMAL, label: 'Normal' },
    { value: VehicleRentalPriority.ALTA, label: 'Alta' },
    { value: VehicleRentalPriority.URGENTE, label: 'Urgente' }
  ];

  // ===== CONFIGURAÇÃO DA TABELA =====
  tableTitle = 'Locações de Veículos';
  showSearch = true;
  searchPlaceholder = 'Buscar por número, cliente ou documento...';
  clickable = true;

  columns = [
    { titulo: 'Número', label: 'rentalNumber' },
    { titulo: 'Cliente', label: 'customerName' },
    { titulo: 'Data Início', label: 'startDate' },
    { titulo: 'Data Fim', label: 'endDate' },
    { titulo: 'Status', label: 'status' },
    { titulo: 'Prioridade', label: 'priority' },
    { titulo: 'Funcionário', label: 'staffName' }
  ];

  actions: TableAction[] = [
    {
      label: 'Visualizar',
      icon: 'fas fa-eye',
      action: 'view',
      class: 'btn-view'
    },
    {
      label: 'Editar',
      icon: 'fas fa-pen-to-square',
      action: 'edit',
      class: 'btn-edit'
    },
    {
      label: 'Iniciar',
      icon: 'fas fa-play',
      action: 'start',
      class: 'btn-success'
    },
    {
      label: 'Finalizar',
      icon: 'fas fa-stop',
      action: 'end',
      class: 'btn-warning'
    },
    {
      label: 'Cancelar',
      icon: 'fas fa-ban',
      action: 'cancel',
      class: 'btn-delete'
    }
  ];

  constructor(
    private vehicleRentalService: VehicleRentalService,
    private errorService: ErrorService,
    private router: Router,
    private statusService: StatusService
  ) {}

  ngOnInit(): void {
    this.loadVehicleRentals();
    this.loadSupportData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    // Limpar timeout ao destruir componente
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  }

  // ===== CARREGAMENTO DE DADOS =====

  /**
   * Carrega todas as locações (paginação backend com filtros)
   */
  loadVehicleRentals(): void {
    this.isLoading = true;
    this.error = null;

    // Preparar filtros para enviar ao backend (apenas os que têm valor)
    const backendFilters: { status?: string; priority?: string; search?: string } = {};
    if (this.filters.status) {
      backendFilters.status = this.filters.status;
    }
    if (this.filters.priority) {
      backendFilters.priority = this.filters.priority;
    }
    if (this.filters.search && this.filters.search.trim()) {
      backendFilters.search = this.filters.search.trim();
    }

    this.vehicleRentalService.getAllVehicleRentalsPaginated(
      this.currentPage, 
      this.pageSize,
      Object.keys(backendFilters).length > 0 ? backendFilters : undefined
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.vehicleRentals = result.items;
          this.filteredVehicleRentals = result.items; // Backend já retorna filtrado
          this.totalItems = result.totalItems;
          this.totalPages = result.totalPages;
          this.currentPage = result.page;
          this.pageSize = result.pageSize;
          this.isLoading = false;
        },
        error: (error) => {
          this.error = 'Erro ao carregar locações. Tente novamente.';
          this.isLoading = false;
          console.error('Erro ao carregar locações:', error);
        }
      });
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadVehicleRentals();
  }

  onPageSizeChange(size: number): void {
    this.pageSize = size;
    this.currentPage = 1;
    this.loadVehicleRentals();
  }

  /**
   * Carrega dados de apoio
   */
  loadSupportData(): void {
    this.loadVehicleStatuses();
  }

  /**
   * Carrega status de veículos dinamicamente
   */
  loadVehicleStatuses(): void {
    this.loadingStatuses = true;
    this.statusService.getByEntity('VehicleRental', true)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (statuses: Status[]) => {
          // Filtrar apenas status com entityType = 'VehicleRental' para garantir
          const vehicleRentalStatuses = statuses.filter(
            status => status.entityType === 'VehicleRental' && status.isActive !== false
          );
          
          this.statusOptions = [
            { value: '', label: 'Todos os Status' },
            ...vehicleRentalStatuses.map(status => ({
              value: status.name,
              label: status.name
            }))
          ];
          this.loadingStatuses = false;
        },
        error: (err) => {
          console.error('Erro ao carregar status de veículos:', err);
          this.loadingStatuses = false;
          // Fallback vazio para não quebrar a interface
          this.statusOptions = [{ value: '', label: 'Todos os Status' }];
        }
      });
  }

  /**
   * Recarrega os dados
   */
  retryLoad(): void {
    this.loadVehicleRentals();
  }

  // ===== FILTROS =====

  /**
   * Aplica filtros - recarrega dados do backend com os filtros
   */
  applyFilters(): void {
    this.currentPage = 1; // Resetar para primeira página ao aplicar filtros
    this.loadVehicleRentals();
  }

  /**
   * Limpa todos os filtros
   */
  clearFilters(): void {
    this.filters = {
      status: '',
      priority: '',
      search: ''
    };
    this.currentPage = 1;
    this.loadVehicleRentals();
  }

  /**
   * Handler para busca da tabela com debounce
   */
  onSearchChange(searchTerm: string): void {
    // Limpar timeout anterior se o usuário continuar digitando
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
    
    // Atualiza o termo de busca imediatamente (para exibição no campo)
    this.filters.search = searchTerm;
    
    // Aguardar 800ms após parar de digitar antes de fazer a requisição
    this.searchTimeout = setTimeout(() => {
      this.currentPage = 1;
      this.loadVehicleRentals();
      this.searchTimeout = null;
    }, 800);
  }


  // ===== AÇÕES DA TABELA =====

  /**
   * Manipula cliques nas ações da tabela
   */
  onActionClick(event: { action: string; item: VehicleRental }): void {
    const { action, item } = event;

    switch (action) {
      case 'view':
        this.viewRental(item.id);
        break;
      case 'edit':
        this.editRental(item.id);
        break;
      case 'start':
        this.startRental(item);
        break;
      case 'end':
        this.endRental(item);
        break;
      case 'cancel':
        this.cancelRental(item);
        break;
      default:
        break;
    }
  }

  // ===== NAVEGAÇÃO =====

  /**
   * Navega para criação de nova locação
   */
  createNewRental(): void {
    this.router.navigate(['/commercial/vehicle-rentals/new']);
  }

  /**
   * Navega para detalhes da locação
   */
  viewRental(rentalId: number): void {
    this.router.navigate(['/commercial/vehicle-rentals', rentalId]);
  }


  /**
   * Navega para edição da locação
   */
  editRental(rentalId: number): void {
    this.router.navigate(['/commercial/vehicle-rentals', rentalId, 'edit']);
  }

  /**
   * Inicia locação
   */
  startRental(rental: VehicleRental): void {
    if (!confirm(`Deseja iniciar a locação ${rental.rentalNumber}?`)) {
      return;
    }

    this.vehicleRentalService.startVehicleRental(rental.id, {})
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.errorService.showInfoMessage('Locação iniciada com sucesso');
          this.loadVehicleRentals();
        },
        error: (error) => {
          this.errorService.handleHttpError(error);
        }
      });
  }

  /**
   * Finaliza locação
   */
  endRental(rental: VehicleRental): void {
    if (!confirm(`Deseja finalizar a locação ${rental.rentalNumber}?`)) {
      return;
    }

    this.vehicleRentalService.endVehicleRental(rental.id, {})
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.errorService.showInfoMessage('Locação finalizada com sucesso');
          this.loadVehicleRentals();
        },
        error: (error) => {
          this.errorService.handleHttpError(error);
        }
      });
  }


  /**
   * Cancela locação
   */
  cancelRental(rental: VehicleRental): void {
    if (!confirm(`Deseja realmente cancelar a locação ${rental.rentalNumber}?`)) {
      return;
    }

    const reason = prompt('Motivo do cancelamento:');
    if (!reason) return;

    this.vehicleRentalService.cancelVehicleRental(rental.id, { reason })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.errorService.showInfoMessage('Locação cancelada com sucesso');
          this.loadVehicleRentals();
        },
        error: (error) => {
          this.errorService.handleHttpError(error);
        }
      });
  }

  // ===== FORMATADORES =====

  /**
   * Formata data para exibição
   */
  formatDate(date: Date | string): string {
    if (!date) return '';
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleDateString('pt-BR');
  }

  /**
   * Formata moeda para exibição
   */
  formatCurrency(value: number): string {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  }

  /**
   * Obtém classe CSS para status
   */
  getStatusClass(status: string): string {
    return this.vehicleRentalService.getStatusColor(status);
  }

  /**
   * Obtém classe CSS para prioridade
   */
  getPriorityClass(priority: string): string {
    return this.vehicleRentalService.getPriorityColor(priority);
  }

  exportToCSV(): void {
    const cols = this.columns;
    const header = cols.map(c => `"${c.titulo}"`).join(',');
    const rows = this.filteredVehicleRentals.map(item =>
      cols.map(c => `"${String((item as any)[c.label] ?? '').replace(/"/g, '""')}"`).join(',')
    );
    const csv = '﻿' + [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'locacoes.csv';
    a.click();
    URL.revokeObjectURL(url);
  }
}
